import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface TenantOption {
  readonly tenantId: string;
  readonly tenantCode: string;
  readonly tenantName: string | null;
}

interface Setting {
  readonly parameterCode: string;
  readonly value: string;
  readonly valueType: string;
  readonly description: string | null;
  readonly minValue: number | null;
  readonly maxValue: number | null;
  readonly defaultValue: string;
  readonly isDefault: boolean;
  readonly group: string;
  readonly modifiedAt: string | null;
}

const BASE = '/v1/platform/general-config';

export function GeneralConfigPage(): ReactNode {
  const { token } = useAuth();
  const [tenants, setTenants] = useState<readonly TenantOption[] | null>(null);
  const [tenantId, setTenantId] = useState('');
  const [rows, setRows] = useState<readonly Setting[] | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<readonly TenantOption[]>(`${BASE}/tenants`, { token })
      .then((r) => {
        if (!alive) return;
        setTenants(r);
        if (r.length > 0) setTenantId(r[0].tenantId);
      })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  const load = useCallback(async (id: string) => {
    if (!id) return;
    setError(null);
    try {
      const r = await apiRequest<readonly Setting[]>(`${BASE}/${id}`, { token });
      setRows(r);
      setDraft(Object.fromEntries(r.map((s) => [s.parameterCode, s.value])));
    } catch (e) { setError((e as Error).message); }
  }, [token]);

  useEffect(() => { void load(tenantId); }, [tenantId, load]);

  const groups = useMemo(() => {
    const map = new Map<string, Setting[]>();
    for (const r of rows ?? []) {
      const list = map.get(r.group) ?? [];
      list.push(r);
      map.set(r.group, list);
    }
    return [...map.entries()];
  }, [rows]);

  async function save(code: string): Promise<void> {
    setBusy(code); setError(null); setStatus(null);
    try {
      const updated = await apiRequest<Setting>(`${BASE}/${tenantId}/${code}`, {
        method: 'PUT', token, body: { value: draft[code] ?? '' },
      });
      setRows((prev) => (prev ?? []).map((r) => (r.parameterCode === code ? updated : r)));
      setDraft((d) => ({ ...d, [code]: updated.value }));
      setStatus(`${code} saved`);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  }

  if (error && !tenants) return <main aria-label="general-config"><p role="alert">{error}</p></main>;
  if (!tenants) return <main aria-label="general-config"><p>Loading…</p></main>;

  return (
    <main aria-label="general-config">
      <h1>General configuration</h1>
      <p style={{ color: 'var(--color-text-muted, #656d76)' }}>
        Security policy per tenant. Changes take effect at the next sign-in.
      </p>

      <section aria-label="tenant-picker" style={{ margin: '16px 0' }}>
        <label htmlFor="tenant-select" style={{ marginRight: 8 }}>Tenant</label>
        <select
          id="tenant-select"
          aria-label="tenant"
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
        >
          {tenants.map((t) => (
            <option key={t.tenantId} value={t.tenantId}>
              {t.tenantCode}{t.tenantName ? ` — ${t.tenantName}` : ''}
            </option>
          ))}
        </select>
      </section>

      {error ? <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p> : null}
      {status ? <p role="status">{status}</p> : null}

      {!rows ? (error ? null : <p>Loading settings…</p>) : rows.length === 0 ? (
        <p aria-label="empty">This tenant has no security settings on record.</p>
      ) : groups.map(([group, items]) => (
        <section key={group} aria-label={`group-${group}`} style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 16, marginBottom: 8 }}>{group}</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-border-default, #d0d7de)', textAlign: 'left' }}>
                <th style={{ padding: '8px 4px' }}>Setting</th>
                <th style={{ padding: '8px 4px' }}>Value</th>
                <th style={{ padding: '8px 4px' }}>Allowed</th>
                <th style={{ padding: '8px 4px' }}>Default</th>
                <th style={{ padding: '8px 4px' }} />
              </tr>
            </thead>
            <tbody>
              {items.map((s) => {
                const dirty = (draft[s.parameterCode] ?? s.value) !== s.value;
                return (
                  <tr key={s.parameterCode} aria-label={`row-${s.parameterCode}`}
                      style={{ borderBottom: '1px solid var(--color-border-default, #d0d7de)' }}>
                    <td style={{ padding: '8px 4px' }}>
                      <div style={{ fontWeight: 600 }}>{s.parameterCode}</div>
                      {s.description ? (
                        <div style={{ fontSize: 12, color: 'var(--color-text-muted, #656d76)' }}>{s.description}</div>
                      ) : null}
                    </td>
                    <td style={{ padding: '8px 4px' }}>
                      {s.valueType === 'BOOLEAN' ? (
                        <select
                          aria-label={`value-${s.parameterCode}`}
                          value={draft[s.parameterCode] ?? s.value}
                          onChange={(e) => setDraft((d) => ({ ...d, [s.parameterCode]: e.target.value }))}
                        >
                          <option value="TRUE">TRUE</option>
                          <option value="FALSE">FALSE</option>
                        </select>
                      ) : (
                        <input
                          aria-label={`value-${s.parameterCode}`}
                          type={s.valueType === 'INT' || s.valueType === 'DECIMAL' ? 'number' : 'text'}
                          value={draft[s.parameterCode] ?? s.value}
                          onChange={(e) => setDraft((d) => ({ ...d, [s.parameterCode]: e.target.value }))}
                          style={{ width: 160 }}
                        />
                      )}
                    </td>
                    <td style={{ padding: '8px 4px', fontSize: 12, color: 'var(--color-text-muted, #656d76)' }}>
                      {s.minValue != null || s.maxValue != null
                        ? `${s.minValue ?? '—'} to ${s.maxValue ?? '—'}`
                        : s.valueType}
                    </td>
                    <td style={{ padding: '8px 4px', fontSize: 12 }}>
                      {s.defaultValue}
                      {s.isDefault ? '' : ' (overridden)'}
                    </td>
                    <td style={{ padding: '8px 4px' }}>
                      <button
                        type="button"
                        aria-label={`save-${s.parameterCode}`}
                        disabled={!dirty || busy === s.parameterCode}
                        onClick={() => void save(s.parameterCode)}
                      >
                        {busy === s.parameterCode ? 'Saving…' : 'Save'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      ))}
    </main>
  );
}
